import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Avatar,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  DirectionsCar as CarIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
  CheckCircle as AvailableIcon,
  Cancel as UnavailableIcon,
  Info as InfoIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { vehicleRequestAPI, vehicleAPI, calendarAPI } from '../services/api';
import { FleetVehicle } from '../types';


interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  status: string;
  requester: string;
  vehicle: string;
}

interface ConflictCheck {
  _id: string;
  startDateTime: string;
  endDateTime: string;
  purpose: string;
  requestedBy: {
    firstName: string;
    lastName: string;
  };
  assignedVehicle?: {
    plateNumber: string;
    make: string;
    model: string;
  };
}

interface VehicleWithAvailability extends FleetVehicle {
  availability?: 'available' | 'unavailable' | 'partially_available';
  conflictingRequests?: Array<{
    requestId: string;
    purpose: string;
    startTime: string;
    endTime: string;
    requester: string;
  }>;
}

interface HourlyData {
  hour: number;
  requestCount: number;
}

interface PeakUsageData {
  hourlyDistribution: HourlyData[];
  insights: {
    peakHour: {
      timeLabel: string;
      requestCount: number;
    };
  };
}

const VehicleCalendarScheduler: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [availableVehicles, setAvailableVehicles] = useState<VehicleWithAvailability[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'executive' | 'staff_bus' | 'technical' | 'general'>('all');
  const [openConflictDialog, setOpenConflictDialog] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleWithAvailability | null>(null);
  const [conflictDetails, setConflictDetails] = useState<ConflictCheck[]>([]);
  const [loading, setLoading] = useState(false);
  const [peakUsageData, setPeakUsageData] = useState<PeakUsageData | null>(null);

  useEffect(() => {
    loadCalendarData();
    loadPeakUsageData();
  }, [selectedDate, selectedCategory]);

  const loadCalendarData = async () => {
    try {
      setLoading(true);
      
      // Get calendar events for the selected date
      const startDate = new Date(selectedDate);
      startDate.setHours(0, 0, 0, 0);
      
      const endDate = new Date(selectedDate);
      endDate.setHours(23, 59, 59, 999);

      const events = await vehicleRequestAPI.getCalendarView(
        startDate.toISOString(),
        endDate.toISOString()
      );
      
      setCalendarEvents(events);
      
      // Load all vehicles and check their availability
      const allVehicles = await vehicleAPI.getAllVehicles({
        category: selectedCategory === 'all' ? undefined : selectedCategory
      });
      
      // Check availability for each vehicle during business hours (8 AM - 6 PM)
      const businessStart = new Date(selectedDate);
      businessStart.setHours(8, 0, 0, 0);
      
      const businessEnd = new Date(selectedDate);
      businessEnd.setHours(18, 0, 0, 0);
      
      const vehicleAvailability = await Promise.all(
        allVehicles.map(async (vehicle) => {
          try {
            // Check for conflicts during business hours
            const conflicts = await calendarAPI.checkConflicts(
              businessStart.toISOString(),
              businessEnd.toISOString()
            );
            
            const vehicleConflicts = conflicts.filter(
              conflict => conflict.assignedVehicle?.plateNumber === vehicle.plateNumber
            );
            
            return {
              ...vehicle,
              availability: vehicleConflicts.length > 0 ? 'unavailable' : 'available' as 'available' | 'unavailable' | 'partially_available',
              conflictingRequests: vehicleConflicts.map(conflict => ({
                requestId: conflict._id,
                purpose: conflict.purpose,
                startTime: conflict.startDateTime,
                endTime: conflict.endDateTime,
                requester: `${conflict.requestedBy.firstName} ${conflict.requestedBy.lastName}`
              }))
            };
          } catch (error) {
            console.error(`Error checking availability for vehicle ${vehicle.plateNumber}:`, error);
            return {
              ...vehicle,
              availability: 'available' as 'available' | 'unavailable' | 'partially_available',
              conflictingRequests: []
            };
          }
        })
      );
      
      setAvailableVehicles(vehicleAvailability);
      
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPeakUsageData = async () => {
    try {
      const startDate = new Date(selectedDate);
      startDate.setDate(startDate.getDate() - 7);
      
      const endDate = new Date(selectedDate);
      endDate.setDate(endDate.getDate() + 7);
      
      const peakData = await calendarAPI.getPeakUsageAnalysis(
        startDate.toISOString(),
        endDate.toISOString()
      );
      
      setPeakUsageData(peakData);
    } catch (error) {
      console.error('Error loading peak usage data:', error);
    }
  };

  const handleVehicleClick = async (vehicle: VehicleWithAvailability) => {
    setSelectedVehicle(vehicle);
    
    if (vehicle.conflictingRequests && vehicle.conflictingRequests.length > 0) {
      // Show conflict details
      setOpenConflictDialog(true);
    }
  };

  const getAvailabilityColor = (availability: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (availability) {
      case 'available': return 'success';
      case 'unavailable': return 'error';
      case 'partially_available': return 'warning';
      default: return 'default';
    }
  };

  const getAvailabilityIcon = (availability: string) => {
    switch (availability) {
      case 'available': return <AvailableIcon />;
      case 'unavailable': return <UnavailableIcon />;
      case 'partially_available': return <WarningIcon />;
      default: return <InfoIcon />;
    }
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getTimeSlotStatus = (hour: number) => {
    if (!peakUsageData) return 'normal';
    
    const hourData = peakUsageData.hourlyDistribution.find((h: HourlyData) => h.hour === hour);
    if (!hourData) return 'normal';
    
    const maxRequests = Math.max(...peakUsageData.hourlyDistribution.map((h: HourlyData) => h.requestCount));
    const percentage = hourData.requestCount / maxRequests;
    
    if (percentage > 0.8) return 'peak';
    if (percentage > 0.5) return 'busy';
    return 'normal';
  };

  const renderTimeSlotIndicator = (hour: number) => {
    const status = getTimeSlotStatus(hour);
    const color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' = 
      status === 'peak' ? 'error' : status === 'busy' ? 'warning' : 'success';
    
    return (
      <Chip
        key={hour}
        size="small"
        color={color}
        label={`${hour}:00`}
        variant={status === 'normal' ? 'outlined' : 'filled'}
        sx={{ minWidth: 60 }}
      />
    );
  };

  const renderVehicleCard = (vehicle: VehicleWithAvailability) => (
    <Card
      key={vehicle.id}
      sx={{
        mb: 2,
        cursor: 'pointer',
        border: vehicle.availability === 'unavailable' ? '1px solid #f44336' : '1px solid #e0e0e0',
        '&:hover': {
          elevation: 4,
          transform: 'translateY(-2px)',
          transition: 'all 0.2s ease-in-out'
        }
      }}
      onClick={() => handleVehicleClick(vehicle)}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar sx={{ 
            bgcolor: vehicle.availability === 'available' ? 'success.main' : 
                    vehicle.availability === 'unavailable' ? 'error.main' : 'warning.main'
          }}>
            {getAvailabilityIcon(vehicle.availability || 'available')}
          </Avatar>
          
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              {vehicle.make} {vehicle.model}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {vehicle.plateNumber} • {vehicle.category}
            </Typography>
            
            {vehicle.conflictingRequests && vehicle.conflictingRequests.length > 0 && (
              <Typography variant="caption" color="error.main">
                {vehicle.conflictingRequests.length} conflict(s) today
              </Typography>
            )}
          </Box>
          
          <Chip
            size="small"
            color={getAvailabilityColor(vehicle.availability || 'available')}
            label={vehicle.availability || 'available'}
            variant="filled"
          />
        </Stack>
      </CardContent>
    </Card>
  );

  const renderConflictDialog = () => (
    <Dialog 
      open={openConflictDialog} 
      onClose={() => setOpenConflictDialog(false)}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        Vehicle Schedule Conflicts
        {selectedVehicle && (
          <Typography variant="subtitle2" color="text.secondary">
            {selectedVehicle.make} {selectedVehicle.model} ({selectedVehicle.plateNumber})
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        {selectedVehicle?.conflictingRequests && selectedVehicle.conflictingRequests.length > 0 ? (
          <List>
            {selectedVehicle.conflictingRequests.map((conflict, index) => (
              <React.Fragment key={conflict.requestId}>
                <ListItem>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'warning.main' }}>
                      <ScheduleIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={conflict.purpose}
                    secondary={
                      <Stack spacing={0.5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon fontSize="small" />
                          <Typography variant="caption">{conflict.requester}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <ScheduleIcon fontSize="small" />
                          <Typography variant="caption">
                            {formatTime(conflict.startTime)} - {formatTime(conflict.endTime)}
                          </Typography>
                        </Box>
                      </Stack>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Chip 
                      size="small" 
                      color="warning" 
                      label="Scheduled" 
                      variant="filled" 
                    />
                  </ListItemSecondaryAction>
                </ListItem>
                {index < selectedVehicle.conflictingRequests!.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Alert severity="success">
            This vehicle is available for the selected date.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpenConflictDialog(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Vehicle Schedule & Availability
        </Typography>
        
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Vehicle Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value as 'all' | 'executive' | 'staff_bus' | 'technical' | 'general')}
            >
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="executive">Executive</MenuItem>
              <MenuItem value="staff_bus">Staff Bus</MenuItem>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="general">General</MenuItem>
            </Select>
          </FormControl>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue || new Date())}
              slotProps={{
                textField: {
                  size: 'small'
                }
              }}
            />
          </LocalizationProvider>
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Peak Usage Insights */}
        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Peak Usage Hours
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Best times to schedule requests based on historical data
              </Typography>
              
              {peakUsageData && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Today's Hour-by-Hour Demand
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                    {[8, 9, 10, 11, 12, 13, 14, 15, 16, 17].map(hour => (
                      renderTimeSlotIndicator(hour)
                    ))}
                  </Stack>
                  
                  <Alert severity="info">
                    <Typography variant="caption">
                      Peak hour: {peakUsageData.insights.peakHour.timeLabel} 
                      ({peakUsageData.insights.peakHour.requestCount} requests)
                    </Typography>
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Daily Schedule Overview */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Today's Schedule Overview
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                {formatDate(selectedDate.toISOString())}
              </Typography>
              
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Total Bookings:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {calendarEvents.length}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Available Vehicles:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {availableVehicles.filter(v => v.availability === 'available').length}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Busy Vehicles:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                    {availableVehicles.filter(v => v.availability === 'unavailable').length}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Vehicle Availability List */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Vehicle Availability - {formatDate(selectedDate.toISOString())}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Click on a vehicle to view detailed schedule information
              </Typography>
              
              {loading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography>Loading vehicle availability...</Typography>
                </Box>
              ) : availableVehicles.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                  <CarIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No vehicles found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your category filter or date selection.
                  </Typography>
                </Paper>
              ) : (
                <Box>
                  {/* Available vehicles first */}
                  {availableVehicles
                    .filter(v => v.availability === 'available')
                    .map(renderVehicleCard)}
                  
                  {/* Divider if both available and unavailable vehicles exist */}
                  {availableVehicles.some(v => v.availability === 'available') &&
                   availableVehicles.some(v => v.availability !== 'available') && (
                    <Divider sx={{ my: 2 }}>
                      <Chip label="Busy Vehicles" size="small" />
                    </Divider>
                  )}
                  
                  {/* Busy vehicles */}
                  {availableVehicles
                    .filter(v => v.availability !== 'available')
                    .map(renderVehicleCard)}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Conflict Details Dialog */}
      {renderConflictDialog()}
    </Box>
  );
};

export default VehicleCalendarScheduler;